/** Build registry, independent of the published city catalogue and the frozen contracts. */
import almaty from "./cities/almaty.json" with { type: "json" };
import astana from "./cities/astana.json" with { type: "json" };

import shymkent from "./cities/shymkent.json" with { type: "json" };

export interface CityBBox {
  south: number;
  west: number;
  north: number;
  east: number;
}
export interface CityPreset extends CityBBox {
  id: string;
  title: string;
  tilesPerSide: number;
}
export type CityConfig = Omit<typeof almaty, "bboxes"> & { bboxes: Record<string, CityPreset> };

/** The registry's structural schema. Reject unknown/missing keys and non-finite numbers. */
function shape(value: unknown, sample: unknown, path: string): void {
  if (Array.isArray(sample)) {
    if (!Array.isArray(value)) throw new Error(`${path}: expected array`);
    for (const item of value) shape(item, sample[0], `${path}[]`);
  } else if (sample !== null && typeof sample === "object") {
    if (value === null || typeof value !== "object" || Array.isArray(value))
      throw new Error(`${path}: expected object`);
    const object = value as Record<string, unknown>;
    const fields = sample as Record<string, unknown>;
    for (const key of Object.keys(object))
      if (!(key in fields)) throw new Error(`${path}.${key}: unknown field`);
    for (const [key, field] of Object.entries(fields)) shape(object[key], field, `${path}.${key}`);
  } else if (
    typeof value !== typeof sample ||
    (typeof value === "number" && !Number.isFinite(value))
  ) {
    throw new Error(`${path}: expected ${typeof sample}`);
  }
}

function validBBox(b: CityBBox): boolean {
  return (
    b.south >= -90 &&
    b.north <= 90 &&
    b.west >= -180 &&
    b.east <= 180 &&
    b.south < b.north &&
    b.west < b.east
  );
}

export function validateCity(value: unknown): CityConfig {
  if (value === null || typeof value !== "object") throw new Error("city: expected object");
  const record = value as Record<string, unknown>;
  // Preset names are dynamic; every preset still has the same strict schema.
  shape({ ...record, bboxes: almaty.bboxes }, almaty, "city");
  if (!record.bboxes || typeof record.bboxes !== "object" || Array.isArray(record.bboxes))
    throw new Error("city.bboxes: expected presets");
  for (const preset of Object.values(record.bboxes)) shape(preset, almaty.bboxes.city, "bbox");
  const city = value as CityConfig;
  if (!["live", "preview"].includes(city.status)) throw new Error("invalid city status");
  if (city.schemaVersion !== 1 || !/^[a-z][a-z0-9-]*$/.test(city.id))
    throw new Error("invalid city version/id");
  if (!/^\d{9}$/.test(city.officialCityId)) throw new Error("invalid officialCityId");
  if (city.bboxId !== `${city.id}-city` || city.graphId !== city.bboxId)
    throw new Error("city graphId/bboxId mismatch");
  if (
    !validBBox(city.bbox) ||
    Math.abs(city.origin.lon - (city.bbox.west + city.bbox.east) / 2) > 1e-9 ||
    Math.abs(city.origin.lat - (city.bbox.south + city.bbox.north) / 2) > 1e-9
  )
    throw new Error("city origin must equal bbox centre");
  for (const preset of Object.values(city.bboxes)) {
    if (
      !validBBox(preset) ||
      !preset.id.startsWith(`${city.id}-`) ||
      !Number.isInteger(preset.tilesPerSide) ||
      preset.tilesPerSide < 1
    )
      throw new Error("invalid city bbox preset");
  }
  const main = city.bboxes.city;
  if (
    !main ||
    main.id !== city.bboxId ||
    ["west", "south", "east", "north"].some(
      (k) => main[k as keyof CityBBox] !== city.bbox[k as keyof CityBBox],
    )
  )
    throw new Error("city preset must equal bbox");
  for (const path of [city.source.pbf, city.source.extract]) {
    if (!/^data\/[a-zA-Z0-9_./-]+$/.test(path) || path.split("/").includes(".."))
      throw new Error("source path must be repository-relative under data/");
  }
  if (!/^[a-f0-9]{64}$/.test(city.source.sha256)) throw new Error("invalid source sha256");
  for (const date of [
    city.generatedAt,
    city.source.replicationTimestamp,
    city.source.extractObjectTimestamp,
  ])
    if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\dZ$/.test(date) || !Number.isFinite(Date.parse(date)))
      throw new Error("invalid source timestamp");
  new Intl.DateTimeFormat("en", { timeZone: city.time.zone });
  if (!Number.isInteger(city.time.utcOffsetMin) || Math.abs(city.time.utcOffsetMin) > 840)
    throw new Error("invalid UTC offset");
  if (
    city.routingPolicy !== "kz-car-assumptions-v1" ||
    city.model.demandPolicy !== "buildings-v1" ||
    !["legacy-v1", "motorway-as-trunk-v1"].includes(city.compiler.highwayPolicy)
  )
    throw new Error("unsupported city policy version");
  if (
    !(city.model.multiplier > 0) ||
    city.model.warmupMinutes < 0 ||
    !Number.isInteger(city.model.slotMinutes) ||
    city.model.slotMinutes < 1 ||
    !Number.isInteger(city.model.slotCount) ||
    city.model.slotCount < 1 ||
    city.model.firstSlotMin < 0 ||
    city.model.firstSlotMin + city.model.slotMinutes * city.model.slotCount > 1440 ||
    city.model.seeds.length === 0 ||
    new Set(city.model.seeds).size !== city.model.seeds.length ||
    city.model.seeds.some((seed) => !Number.isInteger(seed) || seed < 0)
  )
    throw new Error("invalid model slots/seeds/demand");
  if (
    Object.values(city.budgets).some((n) => !Number.isInteger(n) || n <= 0) ||
    city.compiler.signalCollapseM !== 30
  )
    throw new Error("invalid city budget/compiler policy");
  if (city.view.center.length !== 2 || !validBBox(city.auditRegions.center))
    throw new Error("invalid city view/audit region");
  return city;
}

const CITIES: Record<string, CityConfig> = {
  almaty: validateCity(almaty),
  astana: validateCity(astana),
  shymkent: validateCity(shymkent),
};

export function getCity(id = "almaty"): CityConfig {
  const city = CITIES[id];
  if (!city) throw new Error(`unknown city "${id}"; known: ${Object.keys(CITIES).join(", ")}`);
  return structuredClone(city);
}

/** Every registered city in the registry's order (Almaty first), as clones like `getCity`. */
export function listCities(): CityConfig[] {
  return Object.values(CITIES).map((city) => structuredClone(city));
}

export function cityPreset(city: CityConfig, key = "city"): CityPreset {
  const bbox = city.bboxes[key] ?? Object.values(city.bboxes).find((b) => b.id === key);
  if (!bbox) throw new Error(`unknown bbox "${key}" for city ${city.id}`);
  return bbox;
}
